import axios from 'axios';

const STRAPI_URL = (process.env.REACT_APP_STRAPI_URL || 'https://cms.symuliga.pl').replace(/\/$/, '');

const strapi = axios.create({
  baseURL: `${STRAPI_URL}/api`,
});

const CATEGORY_LABELS = {
  analizy: 'Analizy',
  rozgrywki: 'Ligi',
  'jak-to-dziala': 'Technologia',
};

export const blogCategories = [
  { label: 'Wszystkie', slug: 'all' },
  { label: 'Analizy', slug: 'analizy' },
  { label: 'Ligi', slug: 'rozgrywki' },
  { label: 'Technologia', slug: 'jak-to-dziala' },
];

const unwrap = (item) => {
  if (!item) return null;
  return item.attributes ? { id: item.id, documentId: item.documentId, ...item.attributes } : item;
};

const unwrapRelation = (relation) => {
  if (!relation) return null;
  if (relation.data) return unwrap(relation.data);
  return unwrap(relation);
};

const unwrapMedia = (media) => {
  const file = unwrapRelation(media);
  if (!file) return null;

  const url = file.url || file.formats?.large?.url || file.formats?.medium?.url || file.formats?.small?.url;
  if (!url) return null;

  return {
    url: url.startsWith('http') ? url : `${STRAPI_URL}${url}`,
    alternativeText: file.alternativeText || file.caption || '',
    width: file.width,
    height: file.height,
  };
};

const normalizeAuthor = (author) => {
  const normalized = unwrapRelation(author);
  return {
    name: normalized?.name || normalized?.username || normalized?.email || 'SymuLiga',
    avatar: unwrapMedia(normalized?.avatar),
  };
};

const normalizeArticle = (item) => {
  const article = unwrap(item);
  if (!article) return null;

  const category = unwrapRelation(article.category);
  const cover = unwrapMedia(article.cover);

  return {
    id: article.documentId || article.id || article.slug,
    documentId: article.documentId,
    title: article.title || '',
    slug: article.slug || '',
    description: article.description || '',
    blocks: article.blocks || [],
    publishedAt: article.publishedAt,
    updatedAt: article.updatedAt,
    createdAt: article.createdAt,
    cover,
    author: normalizeAuthor(article.author),
    category: category
      ? {
          name: CATEGORY_LABELS[category.slug] || category.name,
          slug: category.slug,
        }
      : null,
  };
};

export const getArticleUrl = (slug) => `${window.location.origin}/blog/${slug}`;

const appendArticlePopulate = (params) => {
  params.set('populate[cover]', 'true');
  params.set('populate[category]', 'true');
  params.set('populate[blocks]', 'true');
  params.set('populate[author][populate][avatar]', 'true');
};

export const fetchArticles = async ({ page = 1, pageSize = 9, category = 'all' } = {}) => {
  const params = new URLSearchParams({
    sort: 'publishedAt:desc',
    'pagination[page]': String(page),
    'pagination[pageSize]': String(pageSize),
  });
  appendArticlePopulate(params);

  if (category && category !== 'all') {
    params.set('filters[category][slug][$eq]', category);
  }

  const response = await strapi.get('/articles', { params });
  const payload = response.data || {};

  return {
    articles: (payload.data || []).map(normalizeArticle).filter(Boolean),
    pagination: payload.meta?.pagination || {
      page,
      pageSize,
      pageCount: 1,
      total: payload.data?.length || 0,
    },
  };
};

export const fetchArticleBySlug = async (slug) => {
  const params = new URLSearchParams({
    'filters[slug][$eq]': slug,
    'pagination[pageSize]': '1',
  });
  appendArticlePopulate(params);

  const response = await strapi.get('/articles', {
    params,
  });

  const article = normalizeArticle(response.data?.data?.[0]);
  if (!article) {
    const error = new Error('Article not found');
    error.status = 404;
    throw error;
  }

  return article;
};

export const formatArticleDate = (date) => {
  if (!date) return '';
  return new Intl.DateTimeFormat('pl-PL', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }).format(new Date(date));
};
